/**
 * @file orderHub.js
 * @version 1.1.2
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-23
 * @description The customer order is the hub of the purchase and sale flow:
 *              Divert and Split act on the ORDER, wherever they are started
 *              from. The screen's copy of utils/orderHub.js on the server
 *              (which refuses the same cases), so the menu items are greyed
 *              with the reason before anyone clicks. Keep the two in step.
 *
 * 1.1.2 (2026-10-23, Cam): Divert is greyed while the order shares tonnage
 * with the customer's other order (utils/tonnageAdditions).
 */

import { isXeroMadePoId } from './poOwnership';
import { isArchivedPoStatus } from './poStatus';
import { suppliedQtyBlock } from './suppliedQty';

/** A diversions value (array, JSON text or nothing) as an array. */
export function diversionsOf(raw) {
  let v = raw;
  if (typeof v === 'string') {
    try { v = JSON.parse(v); } catch { v = null; }
  }
  return Array.isArray(v) ? v.filter(d => d && typeof d === 'object') : [];
}

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/** One diversion in a line: "06/10/2026 14:02 — Old Farm → New Farm, by Amy. Reason: …" */
export function diversionText(d) {
  if (!d) return '';
  return `${when(d.at)} — ${d.from_name || '?'} → ${d.to_name || '?'}${d.by_name ? `, by ${d.by_name}` : ''}.`
    + `${d.reason ? ` Reason: ${d.reason}` : ''}`;
}

/** The hover text for the Diverted pill: every diversion, newest last. */
export function diversionsTitle(list) {
  const all = diversionsOf(list);
  if (!all.length) return '';
  return `Diverted${all.length > 1 ? ` ${all.length} times` : ''}:\n${all.map(diversionText).join('\n')}\nClick for details.`;
}

/** Why this order cannot be diverted, or null (invoiced or archived only). */
export function divertBlockForOrder(order) {
  if (!order) return 'Not raised from a customer order — nothing to divert';
  const num = order.order_number || order.number || 'This order';
  if (order.invoiced_status === 'Invoiced') {
    return `${num} has been invoiced — the invoice is addressed to its customer, so it cannot be diverted`;
  }
  if (order.is_archived) return `${num} is archived — restore it first`;
  // A split order's tonnage added to the customer's other order (or this one
  // taking it): take it back out first (the server refuses it too).
  const ta = order.tonnage_additions || {};
  const live = [...(ta.added_to || []), ...(ta.includes || [])].find(Boolean);
  if (live) {
    const other = live.target_order_number || live.source_order_number;
    return `${num} shares tonnage with ${other} (Added tonnage…) — take it back out first`;
  }
  return null;
}

/** Why this order cannot be split, or null — the order screen's own refusals. */
export function splitBlockForOrder(order, supplyBlock = null) {
  if (!order) return 'Not raised from a customer order — nothing to split';
  if (order.is_archived) return `${order.order_number || order.number || 'The order'} is archived — restore it first`;
  if (order.po_status === 'No POs Linked') return 'Cannot split — no PO created yet';
  if (order.invoiced_status === 'Invoiced') return 'Cannot split — order is invoiced';
  // A draft invoice waiting in Price Orders bills the whole order (server:
  // utils/orderHub.splitBlock — keep in step).
  if (order.draft_invoice_no) {
    return `Cannot split — draft invoice ${order.draft_invoice_no} is waiting in Pricing & Costing; approve it, or set the order back off Delivered (which deletes it)`;
  }
  if (order.is_split) return 'Cannot split — this is a child split order';
  if (order.has_splits) return 'Cannot split — already has split orders';
  if (supplyBlock) return `Cannot split yet — ${supplyBlock}`;
  if (!order.has_supplied_qty) return 'Add supplied quantity first';
  return null;
}

/**
 * The customer order a Search Purchase Orders row's Divert / Split act on.
 * Its own customer order first, else the one order its lines serve.
 * @returns {{ so: object|null, block: string|null }}
 */
export function hubForPoRow(po) {
  if (!po) return { so: null, block: 'Purchase order not found' };
  if (isArchivedPoStatus(po.status)) return { so: null, block: 'This PO is archived' };
  if (isXeroMadePoId(String(po.purchaseOrderID || ''))) {
    return { so: null, block: 'Made in Xero — divert or split its customer order from Search Customer Orders' };
  }
  const sos = Array.isArray(po.linkedSOs) ? po.linkedSOs : [];
  const own = po.customerOrderId ? sos.find(s => Number(s.id) === Number(po.customerOrderId)) : null;
  if (own) return { so: own, block: null };
  if (sos.length === 1) return { so: sos[0], block: null };
  if (sos.length > 1) {
    return { so: null, block: `Serves ${sos.length} customer orders — divert or split each from Search Customer Orders` };
  }
  return { so: null, block: 'Not raised from a customer order (a stock purchase) — nothing to divert or split' };
}

/** Divert from a PO row: the hub's block, then the order's. */
export function divertBlockForPoRow(po) {
  const { so, block } = hubForPoRow(po);
  return block || divertBlockForOrder(so && { ...so, order_number: so.number });
}

/** Split from a PO row: PO-Ds only, then the hub's block, then the order's. */
export function splitBlockForPoRow(po) {
  if (po?.poType === 'haulage') return 'A haulage order follows its customer order — split from the order or its PO-D';
  const { so, block } = hubForPoRow(po);
  if (block) return block;
  const supply = suppliedQtyBlock({
    deliveryDate: so.delivery_date, deliveryStatus: so.delivery_status, terms: so.terms, hasSupplyPos: true
  });
  return splitBlockForOrder({ ...so, order_number: so.number }, supply);
}
